"use client";
import { useMutation, useQuery } from "@apollo/client/react";
import { gql } from "graphql-tag";
import { useReactiveVar } from "@apollo/client/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { captureException, setTag, setUser } from "@sentry/nextjs";
import isEmpty from "lodash/isEmpty";
import { useApolloClient } from "@apollo/client/react";

import { renderFullName } from "@portal/app/_common/lib/formatRenderers";
import { decodeJwt, TokenInfo } from "@portal/app/_common/lib/token";
import { ClientFacingErrors } from "@portal/app/_common/types/clientErrors";
import { Role } from "@portal/app/_common/types/role";
import { detectKnownClientError } from "@portal/app/_common/lib/clientErrorUtils";
import { createGenericContext } from "@portal/app/_common/lib/createGenericContext";
import { useMixpanel } from "@portal/app/_components/hooks/useMixpanel";
import { accessTokenVar, logoutAndGoToLoginPage } from "../apolloUtils";
import {
  MeForAuthContextQuery,
  MeForAuthContextDocument,
  UpdateSessionCustomerDocument,
} from "./generated";
import { hotjar } from "./HotjarProvider";
import { SignInFlowController } from "@portal/app/login/SignInFlowController";
import Modal from "@mui/material/Modal";
import { useRunOnMountEffect } from "@portal/app/_components/hooks/useRunOnMountEffect";
import { COOKIE_KEYS, deleteCookie, getCookie } from "@portal/app/_common/lib/headers";

void gql`
  query meForAuthContext {
    me {
      id
      nameFirst
      nameLast
      phone
      email
      activeRole
      roles
      providers {
        id
        fullName
      }
    }
    viewerCustomer {
      id
      detailsMap {
        id
        referrer
        landingUrl
      }
    }
  }
  mutation updateSessionCustomer($userId: String, $referrer: String, $landingUrl: String) {
    updateSessionCustomer(userId: $userId, referrer: $referrer, landingUrl: $landingUrl) {
      id
      detailsMap {
        id
        referrer
        landingUrl
      }
    }
  }
`;

export type RoleHelpers = {
  /** The current role of the user. If an admin is viewing as a school, the active role is SCHOOL **/
  activeRole: undefined | Role;
  /** Whether user is an admin, based on activeRole **/
  isAdmin: undefined | boolean;
  /** Whether user has the ADMIN role, based on all the user's roles **/
  hasAdminRole: undefined | boolean;
  /** Whether user is a school, based on activeRole **/
  isSchool: undefined | boolean;
  /** Whether user has the SCHOOL role, based on all the user's roles **/
  hasSchoolRole: undefined | boolean;
  /** Whether user is a customer, based on activeRole **/
  isCustomer: undefined | boolean;
  /** Whether user is a school, and only has that role (e.g. not an admin viewing as) **/
  isOnlySchool: undefined | boolean;
  /** Whether user is a customer, and only has that role (e.g. not an admin or school viewing as) **/
  isOnlyCustomer: undefined | boolean;
};

export interface AuthenticatedContextProps {
  loading: boolean;
  loggedIn: boolean;
  role: RoleHelpers;
  userId: string | undefined;
  user: MeForAuthContextQuery["me"] | undefined;
  customerId: string | undefined;
  setIsSignInOpen: React.Dispatch<React.SetStateAction<boolean>>;
  openSignInModal: (type: "SIGN_IN" | "CREATE_ACCOUNT") => void;
}

const [useAuthenticatedContext, AuthenticatedContext] =
  createGenericContext<AuthenticatedContextProps>("AuthenticatedContext");
export { useAuthenticatedContext, AuthenticatedContext };

const defaultRole: RoleHelpers = {
  activeRole: undefined,
  isAdmin: undefined,
  hasAdminRole: undefined,
  isSchool: undefined,
  hasSchoolRole: undefined,
  isCustomer: undefined,
  isOnlySchool: undefined,
  isOnlyCustomer: undefined,
};

const getRoleFromTokenInfo = (tokenInfo: TokenInfo | null | undefined): RoleHelpers =>
  !isEmpty(tokenInfo)
    ? {
        activeRole: tokenInfo.activeRole,
        isAdmin: tokenInfo.activeRole === "ADMIN",
        hasAdminRole: tokenInfo.roles.includes("ADMIN"),
        isSchool: tokenInfo.activeRole === "SCHOOL",
        hasSchoolRole: tokenInfo.roles.includes("SCHOOL"),
        isCustomer: tokenInfo.activeRole === "CUSTOMER",
        isOnlySchool: tokenInfo.activeRole === "SCHOOL" && tokenInfo.roles.length === 1,
        isOnlyCustomer: tokenInfo.activeRole === "CUSTOMER" && tokenInfo.roles.length === 1,
      }
    : defaultRole;

export const AuthenticatedContextProvider = ({
  tokenInfo: ssrTokenInfo,
  children,
}: React.PropsWithChildren<{ tokenInfo: TokenInfo | null | undefined }>) => {
  const analytics = useMixpanel();
  const apolloClient = useApolloClient();
  const accessToken = useReactiveVar(accessTokenVar);
  const decodedToken = decodeJwt<TokenInfo>(accessToken);
  const tokenInfo = !isEmpty(decodedToken) ? decodedToken : ssrTokenInfo;
  const [isLoginOpen, setIsSignInOpen] = useState(false);
  const [signInType, setSignInType] = useState<"SIGN_IN" | "CREATE_ACCOUNT">("SIGN_IN");
  const [errorHandled, setErrorHandled] = useState(false);
  const updateSessionCalled = useRef(false);
  const customerId = tokenInfo?.customerId;
  const userId = tokenInfo?.userId;
  const loggedIn = !!(tokenInfo?.email ?? tokenInfo?.phone ?? userId);

  const [updateSessionCustomer] = useMutation(UpdateSessionCustomerDocument);

  const {
    error,
    data: meData,
    loading,
  } = useQuery(MeForAuthContextDocument, {
    skip: !loggedIn || !accessToken,
  });
  const me = meData?.me;

  useEffect(() => {
    if (!me) {
      return;
    }

    const providers = me.providers;

    /**
     * An alias from customerId to the device ID is first set before login.
     * Upon login, the user ID is set, and aliased to the customer ID, linking all 3.
     */
    analytics.provider.identify(me.id);
    analytics.provider.alias(me.id, customerId);
    analytics.provider.people.set?.({
      customerId: customerId || undefined,
      firstName: me.nameFirst || undefined,
      lastName: me.nameLast || undefined,
      email: me.email || undefined,
      phone: me.phone || undefined,
      providerIds: providers ? providers.map((provider) => provider.id) : undefined,
      role: me.activeRole,
      clientVersion: process.env.DREAM_CLIENT_VERSION,
    });

    hotjar.identify(me.id, {
      customerId: customerId || undefined,
      firstName: me.nameFirst || undefined,
      lastName: me.nameLast || undefined,
      email: me.email || undefined,
      phone: me.phone || undefined,
      providerIds: providers ? providers.map((provider) => provider.id) : undefined,
      role: me.activeRole,
      clientVersion: process.env.DREAM_CLIENT_VERSION,
    });

    setUser({
      id: me.id,
      customerId: customerId,
      email: me.email || undefined,
      phone: me.phone || undefined,
      name: renderFullName(me),
      clientVersion: process.env.DREAM_CLIENT_VERSION,
    });
    /**
     * Fields outside of id, email, ip, and username are not filterable in Sentry.
     * Tags are...
     * Syntax examples:
     *   tags[customer]:"clyi4h1qi0g2y28sb9yq51fox"
     */
    if (customerId) {
      setTag("customer", customerId);
    }
    if (process.env.DREAM_CLIENT_VERSION) {
      setTag("clientVersion", process.env.DREAM_CLIENT_VERSION);
    }
  }, [analytics.provider, customerId, me]);

  useEffect(() => {
    if (!error) {
      setErrorHandled(false);
      return;
    }

    let cancelled = false;
    const handleError = async () => {
      try {
        const clientError = detectKnownClientError(error) as ClientFacingErrors;
        if (clientError === ClientFacingErrors.NotFound) {
          await logoutAndGoToLoginPage();
        } else {
          captureException(error);
        }
      } catch (secondaryError) {
        captureException(secondaryError);
      } finally {
        if (!cancelled) {
          setErrorHandled(true);
        }
      }
    };

    void handleError();
    return () => {
      cancelled = true;
    };
  }, [error]);

  useEffect(() => {
    if (
      tokenInfo?.activeRole === "CUSTOMER" &&
      meData &&
      !updateSessionCalled.current &&
      ((!meData.viewerCustomer?.detailsMap.referrer && getCookie(COOKIE_KEYS.INITIAL_REFERRER)) ||
        (!meData.viewerCustomer?.detailsMap.landingUrl && getCookie(COOKIE_KEYS.LANDING_URL)))
    ) {
      updateSessionCalled.current = true;
      void updateSessionCustomer({
        variables: {
          referrer: getCookie(COOKIE_KEYS.INITIAL_REFERRER),
          landingUrl: getCookie(COOKIE_KEYS.LANDING_URL),
        },
      });
    }
  }, [meData, tokenInfo?.activeRole, updateSessionCustomer]);

  useRunOnMountEffect(() => {
    if (customerId) {
      analytics.provider.alias(customerId);
    }
    if (getCookie(COOKIE_KEYS.LOGOUT)) {
      analytics.provider.reset();
      void apolloClient.resetStore();
      deleteCookie(COOKIE_KEYS.LOGOUT);
    }
  });

  const onSignInClose = useCallback(() => setIsSignInOpen(false), []);
  const openSignInModal = useCallback((type: "SIGN_IN" | "CREATE_ACCOUNT") => {
    setSignInType(type);
    setIsSignInOpen(true);
  }, []);

  if (error) {
    // Effects do not run during SSR. In the browser, wait until the error has
    // been reported (or the stale session has been cleared) before handing it
    // to the nearest error boundary.
    if (typeof document !== "undefined" && !errorHandled) {
      return null;
    }
    throw new Error(error?.message || "An error occurred. Please refresh and try again.");
  }

  return (
    <AuthenticatedContext.Provider
      value={{
        loading: loading || (loggedIn && !meData),
        loggedIn,
        role: getRoleFromTokenInfo(tokenInfo),
        userId,
        user: meData?.me,
        customerId,
        setIsSignInOpen,
        openSignInModal,
      }}
    >
      {children}
      <Modal open={isLoginOpen} onClose={onSignInClose}>
        <SignInFlowController onLoginSuccess={onSignInClose} createAccountOrSignIn={signInType} />
      </Modal>
    </AuthenticatedContext.Provider>
  );
};
